import React from 'react';

/**
 * DocEditorToolbar — the editor's action toolbar. Portaled by DocEditor into
 * the hosting surface's `.doc-editor-toolbar-slot` (doc page top bar,
 * mission doc context bar) so document actions live in the chrome, not in
 * the document body. Buttons follow the `.doc-toolbar-btn` pattern — new
 * actions slot in as more <button>s here.
 */
export const DocEditorToolbar = ({
  onUncheckAll,
  canUncheckAll = false,
  onEmptyVariableValues,
  canEmptyVariableValues = false,
  onToggleInlineCode,
  onFind,
  /** Host chrome shell while the editor loads — same buttons, inert. */
  disabled = false,
}) => {
  return (
    <div
      className="doc-editor-toolbar"
      role="toolbar"
      aria-label="Document actions"
      aria-disabled={disabled || undefined}
    >
      {(onFind || disabled) && (
        <button
          type="button"
          className="doc-toolbar-btn"
          onClick={onFind}
          disabled={disabled || !onFind}
          title="Find in doc (Ctrl+F)"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          Find
        </button>
      )}
      {(onToggleInlineCode || disabled) && (
        <button
          type="button"
          className="doc-toolbar-btn"
          // mousedown + preventDefault keeps the text selection in the
          // contenteditable (a click would steal focus and collapse it)
          onMouseDown={(e) => {
            e.preventDefault();
            if (!disabled) onToggleInlineCode?.();
          }}
          disabled={disabled || !onToggleInlineCode}
          title="Inline code (Ctrl+E)"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
          Code
        </button>
      )}
      <button
        type="button"
        className="doc-toolbar-btn"
        // Keep editor focus so Ctrl+Z can undo this action (toolbar is portaled
        // outside .doc-editor; a normal click would steal focus and disarm undo).
        onMouseDown={(e) => e.preventDefault()}
        onClick={onUncheckAll}
        disabled={disabled || !canUncheckAll}
        title={
          canUncheckAll
            ? 'Uncheck every checkbox in this doc'
            : 'No checked checkboxes'
        }
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="4" />
          <line x1="9" y1="12" x2="15" y2="12" />
        </svg>
        Uncheck All Checkboxes
      </button>
      <button
        type="button"
        className="doc-toolbar-btn"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onEmptyVariableValues}
        disabled={disabled || !canEmptyVariableValues}
        title={
          canEmptyVariableValues
            ? 'Empty unlocked variable values set here. Locked values and inherited variables stay.'
            : 'No variable values to empty here'
        }
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 5c-2.2 0-3.5 1.8-3.5 7s1.3 7 3.5 7" />
          <path d="M16 5c2.2 0 3.5 1.8 3.5 7s-1.3 7-3.5 7" />
        </svg>
        Empty All Variable Values
      </button>
    </div>
  );
};
